import { FormField, FormHelp, UnstyledButton } from '@zillow/constellation-10';
import { Adornment, Combobox, IconChevronDown, IconChevronUp, IconCloseCircle } from '@zillow/constellation';
import { css } from '@/styled-system/css';
import { Fragment, useContext } from 'react';
import { Skeleton, SkeletonListItem } from './Skeleton';
import { StyledAdornedInput, StyledComboboxOption, StyledInput } from './StyledComponents';
import usePropertyFilter, { PropertyListingItem, UpdatePathWithListingAlias } from '../../hooks/usePropertyFilter';
import { IsVisible } from '@zg-rentals/react-use';
import { MultiFamilyLeadsPageContext } from '../../contexts/MultiFamilyLeadsContextProvider';
import { useRouter } from 'next/router';

type ComboboxInputProps = {
  expanded: boolean;
  onClear?(): void;
  onTagClose?(): void;
  options: PropertyListingItem[] | [];
};

type PropertyDropdownProps = {
  includeMultifamily?: boolean;
  includeSingleFamily?: boolean;
};

export const formFieldStyles = css.raw({
  minWidth: '309px',
  '& > ul': {
    maxHeight: '268px',
  },
});

const PropertyDropdown = ({ includeMultifamily, includeSingleFamily }: PropertyDropdownProps) => {
  const router = useRouter();
  const { listingAlias } = useContext(MultiFamilyLeadsPageContext);

  const updatePathWithListingAlias: UpdatePathWithListingAlias = (listingAlias) => {
    const updatedRouter = {
      pathname: router.pathname,
      query: {
        ...router.query,
        listingAlias,
      },
    };
    router.push(updatedRouter);
  };

  const resetPropertyFilter = () => {
    delete router.query.listingAlias;
    router.push({
      pathname: router.pathname,
      query: router.query,
    });
  };

  const {
    errorMessage,
    handleChange,
    handleScroll,
    handleSelectProperty,
    hasMoreToPaginate,
    highlightPatternRegex,
    propertyListings,
    searchText,
    shouldShowSkeleton,
  } = usePropertyFilter({
    defaultListingAliasOnLoad: listingAlias,
    resetPropertyFilter,
    updatePathWithListingAlias,
    includeMultifamily,
    includeSingleFamily,
  });

  const isLoadingPropertyAddress = listingAlias && shouldShowSkeleton;

  return (
    <FormField
      error={!!errorMessage}
      description={<FormHelp>{errorMessage}</FormHelp>}
      css={formFieldStyles}
      control={
        isLoadingPropertyAddress ? (
          <Skeleton />
        ) : (
          <Combobox
            autoCompleteBehavior="manual"
            fluidDropdown={false}
            focusFirstOption={true}
            freeForm
            onChange={handleChange}
            onOptionSelect={handleSelectProperty}
            optionFilter={null} // Filtering happens at the API level
            options={propertyListings}
            value={searchText}
            /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
            renderInput={({ expanded, onClear, onTagClose, ...rest }: ComboboxInputProps) => {
              return (
                <StyledAdornedInput
                  input={<StyledInput placeholder="Enter a property" {...rest} />}
                  rightAdornment={
                    <Adornment>
                      {searchText ? (
                        <UnstyledButton onClick={onClear} title="Clear" css={{ display: 'flex' }}>
                          <IconCloseCircle />
                        </UnstyledButton>
                      ) : (
                        <>{expanded ? <IconChevronUp /> : <IconChevronDown />}</>
                      )}
                    </Adornment>
                  }
                />
              );
            }}
            renderOption={(props: { [key: string]: any }) => {
              const { id } = props;
              const isLastItemInList = id === propertyListings[propertyListings.length - 1].id;

              return (
                <Fragment key={id}>
                  <StyledComboboxOption {...props} highlightPattern={highlightPatternRegex} />

                  {isLastItemInList && hasMoreToPaginate && (
                    <IsVisible onChange={handleScroll} options={{ rootMargin: '0px 0px -200px 0px' }}>
                      {({ ref }) => <SkeletonListItem ref={ref} />}
                    </IsVisible>
                  )}
                </Fragment>
              );
            }}
            size="lg"
          />
        )
      }
    />
  );
};

export default PropertyDropdown;
